import { useState } from "react";
import { useStepMutations, type StepRow } from "../../hooks/usePlay";
import { ApiError } from "../../api/client";
import type { Persona } from "../../hooks/usePersona";

interface StepActionsProps {
  step: StepRow;
  workspaceId: string;
  playId: string;
  currentUserId: string;
  persona: Persona | null;
  /** Returns the validated output object to send to /complete, or null to block. */
  buildOutput?: () => Record<string, unknown> | null;
  /**
   * When set, the parent play belongs to a GamePlan — Complete shows an
   * optional "Add to Campaign Context" annotation form (Phase 2 / UC-04).
   * Hidden otherwise per US-10 AC-04.
   */
}

function canClaim(step: StepRow, currentUserId: string): boolean {
  return (
    step.state === "ready" &&
    step.ownerType === "operator" &&
    (step.ownerUserId === null || step.ownerUserId === currentUserId)
  );
}

function canComplete(step: StepRow, currentUserId: string): boolean {
  return (
    step.state === "running" &&
    step.ownerUserId === currentUserId
  );
}

function canFail(step: StepRow, currentUserId: string): boolean {
  return (
    step.state === "running" &&
    step.ownerUserId === currentUserId
  );
}

function canReject(persona: Persona | null): boolean {
  return persona === "approver" || persona === "cxo" || persona === "owner";
}

export function StepActions({
  step,
  workspaceId,
  playId,
  currentUserId,
  persona,
  buildOutput,
}: StepActionsProps) {
  const m = useStepMutations(workspaceId, playId);
  const [actionError, setActionError] = useState<string | null>(null);

  const claimable = canClaim(step, currentUserId);
  const completable = canComplete(step, currentUserId);
  const failable = canFail(step, currentUserId);
  const rejectable =
    canReject(persona) && step.state === "awaiting_review";

  const onClaim = async () => {
    setActionError(null);
    try {
      await m.claim.mutateAsync(step.id);
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? `claim failed: ${e.status}`
          : (e as Error).message,
      );
    }
  };

  const onComplete = async () => {
    setActionError(null);
    const out = buildOutput ? buildOutput() : {};
    if (out === null) return;
    try {
      await m.complete.mutateAsync({
        stepId: step.id,
        output: out,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setActionError(
          `complete failed: ${e.status} ${typeof e.body === "object" ? JSON.stringify(e.body) : ""}`,
        );
      } else {
        setActionError((e as Error).message);
      }
    }
  };

  const onFail = async () => {
    setActionError(null);
    const reason = window.prompt("Failure reason?") ?? "";
    if (!reason) return;
    try {
      await m.fail.mutateAsync({
        stepId: step.id,
        error: reason,
        retry: false,
      });
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  const onReject = async () => {
    setActionError(null);
    const reason = window.prompt("Reject reason?") ?? "";
    if (!reason) return;
    try {
      await m.reject.mutateAsync({ stepId: step.id, reason });
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  if (!claimable && !completable && !failable && !rejectable) return null;


  return (
    <div className="ho-actions" data-testid="step-actions">
      {claimable ? (
        <button
          type="button"
          className="btn primary"
          onClick={onClaim}
          disabled={m.claim.isPending}
          data-testid="step-claim-btn"
        >
          Claim
        </button>
      ) : null}
      {completable ? (
        <button
          type="button"
          className="btn primary"
          onClick={onComplete}
          disabled={m.complete.isPending}
          data-testid="step-complete-btn"
        >
          Complete
        </button>
      ) : null}
      {failable ? (
        <button
          type="button"
          className="btn"
          onClick={onFail}
          disabled={m.fail.isPending}
          data-testid="step-fail-btn"
        >
          Fail
        </button>
      ) : null}
      {rejectable ? (
        <button
          type="button"
          className="btn"
          onClick={onReject}
          disabled={m.reject.isPending}
          data-testid="step-reject-btn"
        >
          Reject
        </button>
      ) : null}
      {actionError ? (
        <span
          style={{ color: "var(--danger)", fontSize: 12, marginLeft: 8 }}
          data-testid="step-action-error"
        >
          {actionError}
        </span>
      ) : null}
    </div>
  );
}
